import type { ChatSidebarScopeTab } from 'utils/chat-sidebar-unified-statuses';

export type ActiveTabId = 'mine' | 'team' | 'all' | `custom:${string}`;

/** נראות שורות ברשימת הסטטוסים בלבד (מקומי) */
export type SidebarStatusVisibility = {
  statusRows: Record<string, boolean>;
};

const VIS_KEY_V1 = 'funner.chatSidebar.statusVisibility.v1';
const VIS_KEY_V2 = 'funner.chatSidebar.statusVisibility.v2';
const TAB_KEY = 'funner.chatSidebar.activeTabId.v2';
const LAST_SCOPE_KEY = 'funner.chatSidebar.lastBuiltinScope.v1';

const DEFAULT_STATUS_ROWS: Record<string, boolean> = {
  allConversations: true,
  awaitingReply: true,
  unassigned: true,
  awaitingCustomer: true,
  firstReply: true,
  reminder: true,
  completed: true,
};

export const DEFAULT_STATUS_VISIBILITY: SidebarStatusVisibility = {
  statusRows: { ...DEFAULT_STATUS_ROWS },
};

/** נראות שורות נפרדת לכל טאב מובנה (שלי / צוות / כל השיחות) */
export type PerScopeStatusVisibility = Record<ChatSidebarScopeTab, SidebarStatusVisibility>;

function mergeRows(partial?: Partial<SidebarStatusVisibility>): SidebarStatusVisibility {
  return {
    statusRows: { ...DEFAULT_STATUS_ROWS, ...partial?.statusRows },
  };
}

function loadLegacyV1Single(): SidebarStatusVisibility | null {
  try {
    const raw = localStorage.getItem(VIS_KEY_V1);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<SidebarStatusVisibility>;
    return mergeRows(p);
  } catch {
    return null;
  }
}

export function loadPerScopeStatusVisibility(): PerScopeStatusVisibility {
  try {
    const raw = localStorage.getItem(VIS_KEY_V2);
    if (raw) {
      const p = JSON.parse(raw) as Partial<PerScopeStatusVisibility>;
      return {
        mine: mergeRows(p.mine),
        team: mergeRows(p.team),
        all: mergeRows(p.all),
      };
    }
  } catch {
    /* fall through */
  }
  const legacy = loadLegacyV1Single();
  if (legacy) {
    const next: PerScopeStatusVisibility = {
      mine: { statusRows: { ...legacy.statusRows } },
      team: { statusRows: { ...legacy.statusRows } },
      all: { statusRows: { ...legacy.statusRows } },
    };
    savePerScopeStatusVisibility(next);
    return next;
  }
  const base = mergeRows();
  const rows = { ...base.statusRows };
  return {
    mine: { statusRows: { ...rows } },
    team: { statusRows: { ...rows } },
    all: { statusRows: { ...rows } },
  };
}

export function savePerScopeStatusVisibility(v: PerScopeStatusVisibility): void {
  try {
    localStorage.setItem(VIS_KEY_V2, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}

function isBuiltin(s: string): s is 'mine' | 'team' | 'all' {
  return s === 'mine' || s === 'team' || s === 'all';
}

export function loadActiveTabId(): ActiveTabId {
  try {
    const raw = localStorage.getItem(TAB_KEY);
    if (raw?.startsWith('custom:')) return raw as ActiveTabId;
    if (raw && isBuiltin(raw)) return raw;
  } catch {
    /* ignore */
  }
  return 'mine';
}

export function saveActiveTabId(id: ActiveTabId): void {
  try {
    localStorage.setItem(TAB_KEY, id);
  } catch {
    /* ignore */
  }
}

export function loadLastBuiltinScope(): ChatSidebarScopeTab {
  try {
    const raw = localStorage.getItem(LAST_SCOPE_KEY);
    if (raw === 'mine' || raw === 'all') return raw;
    if (raw === 'team') return 'mine';
  } catch {
    /* ignore */
  }
  return 'mine';
}

export function saveLastBuiltinScope(scope: ChatSidebarScopeTab): void {
  try {
    localStorage.setItem(LAST_SCOPE_KEY, scope);
  } catch {
    /* ignore */
  }
}
